<template>
  <div
    v-show="shouldShow"
    class="orientation-warning"
    aria-live="polite"
    role="status"
    :style="inlineSafeArea"
  >
    <div class="orientation-warning__inner">
      <p class="orientation-warning__text">{{ displayMessage }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";

type Msgs = {
  phoneVertical?: string; // mensaje cuando queremos VERTICAL en teléfono
  phoneHorizontal?: string; // mensaje cuando queremos HORIZONTAL en teléfono
  tabletDesktopVertical?: string; // mensaje cuando queremos VERTICAL en tablet/desktop
  tabletDesktopHorizontal?: string; // mensaje cuando queremos HORIZONTAL en tablet/desktop
};

const props = withDefaults(
  defineProps<{
    /** Política: auto (phone vs tablet/desktop) o solo móviles */
    scope?: "auto" | "mobile-only";
    /** Textos opcionales por caso */
    messages?: Msgs;
    /** Prioridad del overlay sobre modales Ionic */
    zIndex?: number;
  }>(),
  {
    scope: "auto",
    messages: () => ({}),
    zIndex: 100003,
  }
);

const emit = defineEmits<{
  (e: "visible-change", value: boolean): void;
}>();

/* ── Detección de dispositivo ──────────────────────────────────────────── */
const isPhone = ref(false);
const isTablet = ref(false);
const isDesktop = ref(false);

function detectDeviceKind() {
  const ua = navigator.userAgent || "";
  const hasTouch = navigator.maxTouchPoints && navigator.maxTouchPoints > 0;
  const coarse = matchMedia("(any-pointer: coarse)").matches;

  const isIpadOS =
    /iPad/i.test(ua) ||
    (navigator.platform === "MacIntel" && hasTouch) ||
    /iPadOS/i.test(ua);
  const isIphone = /iPhone/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  const isAndroidMobile = isAndroid && /Mobile/i.test(ua);
  const isAndroidTablet = isAndroid && !/Mobile/i.test(ua);

  const shortSide = Math.min(window.innerWidth, window.innerHeight);
  const looksPhone = shortSide <= 480;

  if (isIpadOS || isAndroidTablet) {
    isPhone.value = false;
    isTablet.value = true;
    isDesktop.value = false;
    return;
  }
  if (isIphone || isAndroidMobile) {
    isPhone.value = true;
    isTablet.value = false;
    isDesktop.value = false;
    return;
  }
  if (coarse) {
    isPhone.value = looksPhone;
    isTablet.value = !looksPhone;
    isDesktop.value = false;
    return;
  }
  isPhone.value = false;
  isTablet.value = false;
  isDesktop.value = true;
}

/* ── Orientación ───────────────────────────────────────────────────────── */
const isPortrait = ref(true);

function detectPortrait(): boolean {
  const mql = window.matchMedia("(orientation: portrait)");
  return mql.matches || window.innerHeight >= window.innerWidth;
}

let mqlPortrait: MediaQueryList | null = null;
let mqlLandscape: MediaQueryList | null = null;

const handleMql = () => (isPortrait.value = detectPortrait());
const handleResize = () =>
  (isPortrait.value = window.innerHeight >= window.innerWidth);
const handleOrientationChange = () => (isPortrait.value = detectPortrait());
const handleVvResize = () => (isPortrait.value = detectPortrait());

/* ── Política efectiva ─────────────────────────────────────────────────── */
/** scope=auto:
 *    phone  => bloquear landscape (pedir vertical)
 *    tablet/desktop => bloquear portrait (pedir horizontal)
 *  scope=mobile-only:
 *    solo teléfonos => bloquear landscape
 */
const effectiveBlockOn = computed<"portrait" | "landscape">(() => {
  if (props.scope === "mobile-only") return "landscape";
  return isPhone.value ? "landscape" : "portrait";
});

const shouldShow = computed(() => {
  if (props.scope === "mobile-only" && !isPhone.value) return false;
  return effectiveBlockOn.value === "portrait"
    ? isPortrait.value
    : !isPortrait.value;
});

/* ── Mensaje dinámico ──────────────────────────────────────────────────── */
const displayMessage = computed(() => {
  const m = props.messages || {};
  // Queremos VERTICAL cuando bloqueamos landscape; queremos HORIZONTAL cuando bloqueamos portrait
  const wantPortrait = effectiveBlockOn.value === "landscape";

  if (isPhone.value) {
    return wantPortrait
      ? (m.phoneVertical ?? "Por favor, usa el dispositivo en vertical")
      : (m.phoneHorizontal ?? "Por favor, usa el dispositivo en horizontal");
  } else {
    return wantPortrait
      ? (m.tabletDesktopVertical ?? "Por favor, usa el dispositivo en vertical")
      : (m.tabletDesktopHorizontal ??
          "Por favor, usa el dispositivo en horizontal");
  }
});

/* ── Safe-areas + z-index ──────────────────────────────────────────────── */
const inlineSafeArea = computed(() => ({
  zIndex: String(props.zIndex),
}));

/* ── Bloqueo de scroll del body + fix zoom iOS ─────────────────────────── */
let savedScrollY = 0;
const afterRotateFix = () => setTimeout(() => window.scrollTo(0, 0), 0);

watch(shouldShow, (v) => {
  const body = document.body;
  if (v) {
    savedScrollY = window.scrollY || 0;
    body.classList.add("orientation-lock--active");
    body.style.top = `-${savedScrollY}px`;
  } else {
    body.classList.remove("orientation-lock--active");
    body.style.top = "";
    window.scrollTo(0, savedScrollY);
  }
  emit("visible-change", v);
});

/* ── Lifecycle ─────────────────────────────────────────────────────────── */
onMounted(() => {
  detectDeviceKind();
  isPortrait.value = detectPortrait();

  mqlPortrait = window.matchMedia("(orientation: portrait)");
  mqlLandscape = window.matchMedia("(orientation: landscape)");
  mqlPortrait.addEventListener?.("change", handleMql);
  mqlLandscape.addEventListener?.("change", handleMql);

  window.addEventListener("resize", handleResize, { passive: true });
  window.addEventListener("orientationchange", handleOrientationChange, {
    passive: true,
  });
  window.addEventListener("orientationchange", afterRotateFix, {
    passive: true,
  });
  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", handleVvResize, {
      passive: true,
    });
  }
  emit("visible-change", shouldShow.value);
});

onBeforeUnmount(() => {
  mqlPortrait?.removeEventListener?.("change", handleMql);
  mqlLandscape?.removeEventListener?.("change", handleMql);
  window.removeEventListener("resize", handleResize);
  window.removeEventListener("orientationchange", handleOrientationChange);
  window.removeEventListener("orientationchange", afterRotateFix);
  if (window.visualViewport) {
    window.visualViewport.removeEventListener("resize", handleVvResize);
  }
});
</script>

<style scoped>
/* Bloqueo del body cuando el overlay está activo */
:global(body.orientation-lock--active) {
  position: fixed;
  width: 100%;
  overflow: hidden;
}

/* Contenedor a pantalla completa con svh/dvh */
.orientation-warning {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh; /* fallback */
  height: 100svh; /* iOS 16+ */
}
@supports (height: 100dvh) {
  .orientation-warning {
    height: 100dvh;
  }
}

/* Fondo y centrado estable */
.orientation-warning {
  display: grid;
  place-items: center;
  background: rgba(0, 0, 0, 0.96);
  color: #fff;
  text-align: center;
  box-sizing: border-box;
  -webkit-font-smoothing: antialiased;
  z-index: 2147483647;
  backface-visibility: hidden;
  transform: translateZ(0);
  overflow: hidden; /* evita scroll accidental del overlay */
}

/* Wrapper interno con safe-areas */
.orientation-warning__inner {
  max-width: 24rem;
  margin: 0 auto;
  padding: 0 16px;
  padding-top: max(
    16px,
    constant(safe-area-inset-top),
    env(safe-area-inset-top)
  );
  padding-bottom: max(
    16px,
    constant(safe-area-inset-bottom),
    env(safe-area-inset-bottom)
  );
  padding-left: max(
    16px,
    constant(safe-area-inset-left),
    env(safe-area-inset-left)
  );
  padding-right: max(
    16px,
    constant(safe-area-inset-right),
    env(safe-area-inset-right)
  );
}

/* Tipografía estable (vmin) */
.orientation-warning__text {
  margin: 0;
  line-height: 1.3;
  font-weight: 700;
  font-size: clamp(16px, 2.6vmin, 22px);
  text-wrap: balance;
  overflow-wrap: anywhere;
}

@supports (padding: env(safe-area-inset-top)) {
  @media (display-mode: standalone) {
    .orientation-warning__text {
      font-size: clamp(16px, 2.4vmin, 20px);
    }
  }
}
</style>
